import React, { useEffect, useState } from 'react';
import Button from '../../../../../component/ui/Button';
import limitIcon from '../../../../../assets/image/subscription/limits-icon.svg';
import advFeatureIcon from '../../../../../assets/image/subscription/adv-feature-icon.svg';
import priorityIcon from '../../../../../assets/image/subscription/priority-support-icon.svg';
import safeSecureIcon from '../../../../../assets/image/subscription/safe-secure-icon.svg';
import checkIcon from '../../../../../assets/image/check_stock_icon.svg';
import CurrentPlanComponents from './componets/currentPlanComponents';
import PlanBenefit from './componets/planBenefit';
import SubscriptionStatisticsUsages from './componets/subscriptionStatisticsUsages';
import SubscriptionFeatures from './componets/subscriptionFeatures';
import CancelSubscriptionModel from './componets/cancelSubscriptionModel';
import CancelSubscriptionReasonModal from './componets/cancelSubscriptionReasonModal';
import ManageBilling from './manageBilling';
import CustomToastMessage from '../../../../components/customToastMessage';
import { getSubscriptions } from '../../../../../services/settings/subscriptions';
import PricingModal from '../../../../../component/PricingModal';
import UsageOverviewModal from './componets/usageOverviewModal';
import PageLoader from '../../../../../component/PageLoader';
import { toast } from 'react-toastify';
import { usePayment } from '../../../../../hooks/usePayment';
import { axiosInstanceV2 } from '../../../../../services/axios';

export default function Subscription({ activeTab }) {
	const [isOpenUpgradeModal, setIsOpenUpgradeModal] = useState(false);
	const [isOpenCancelSubscriptionModal, setIsOpenCancelSubscriptionModal] = useState(false);
	const [isOpenReasonModal, setIsOpenReasonModal] = useState(false);
	const [isShowToast, setShowToast] = useState(false);
	const [isloadPlan, setIsLoadPlan] = useState(false);
	const [isOpenUsageOverviewModal, setIsOpenUsageOverviewModal] = useState(false);
	const [isloadSubscription, setIsLoadSubscription] = useState(false);
	const [subscriptionScreenType, setSubscriptionScreenType] = useState('subscription');
	const [planBenefit, setPlanBenefit] = useState([
		{
			icon: limitIcon,
			title: 'Higher Limits',
			description: 'Increase invoices, users, and storage as you grow.',
		},
		{
			icon: advFeatureIcon,
			title: 'Advanced Features',
			description: 'Unlock advanced reports, analytics, and automation.',
		},
		{
			icon: priorityIcon,
			title: 'Priority Support',
			description: 'Get faster response with priority support.',
		},
		{
			icon: safeSecureIcon,
			title: 'Safe & Secure',
			description: 'Your data is protected and backed up.',
		},
	]);
	const [subscriptionDetails, setSubscriptionDetails] = useState({});
	const [loadError, setLoadError] = useState(null);
	const { openSubscriptionCheckout } = usePayment();
	const isPendingPayment = ['created', 'authenticated'].includes(subscriptionDetails?.status);

	const getSubscriptionDetails = async () => {
		try {
			setIsLoadSubscription(true);
			setLoadError(null);
			const response = await getSubscriptions();
			// `?? {}` matters: the response can legitimately arrive without a
			// `subscription` key, and storing `undefined` made every later
			// property read on it throw during render.
			setSubscriptionDetails(response?.subscription ?? {});
		} catch (error) {
			// This catch used to be empty, so a failed load left the page to
			// render against missing data and crash with an unhandled TypeError
			// instead of telling the user anything.
			console.error('Failed to load subscription details:', error);
			setLoadError(error);
			setSubscriptionDetails({});
		} finally {
			setIsLoadSubscription(false);
		}
	};

	useEffect(() => {
		getSubscriptionDetails();
	}, []);

	const showMessage = () => {
		setShowToast(true);
		setTimeout(() => {
			setShowToast(false);
		}, 3000);
	};

	// service
	const handleCompletePayment = async () => {
		try {
			const token = localStorage.getItem('authToken');
			const response = await axiosInstanceV2.get('/subscription/pending', {
				headers: { Authorization: `Bearer ${token}` },
			});
			const payload = response.data.data;
			await openSubscriptionCheckout({
				subscription: payload,
				billingDetails: subscriptionDetails.billingDetails,
				paymentMethod: subscriptionDetails.payment_details?.method,
			});
		} catch (error) {
			toast.error(error?.response?.data?.message ?? 'Unable to resume payment');
		}
	};

	return isloadSubscription ? (
		<PageLoader />
	) : subscriptionScreenType === 'subscription' ? (
		<div>
			<PricingModal
				isOpen={isOpenUpgradeModal}
				handleClose={() => setIsOpenUpgradeModal(false)}
			/>
			<div style={{ backgroundColor: '#EDEDF7', position: 'sticky', top: 0, zIndex: 100 }}>
				<div className="d-flex py-3 px-2 justify-content-between align-items-center">
					<div className="fs-3 fw-bold">{'Subscription & Billing'}</div>
					<div className="d-flex gap-3 px-3">
						{isPendingPayment ? (
							<Button
								type="button"
								className="btn-primary fs-5 fw-light py-1 px-4"
								button="Complete Payment"
								onClick={handleCompletePayment}
							/>
						) : (
							<Button
								type="button"
								className="mt-0 btn-primary fs-5 fw-light add-pro py-1"
								button="Upgrade"
								onClick={() => setIsOpenUpgradeModal(true)}
							/>
						)}
					</div>
				</div>
			</div>
			<div style={{ height: 'calc(100vh - 120px)', overflowX: 'auto', overflowY: 'auto' }}>
				{loadError ? (
					// A failed load now renders an explanation instead of letting the
					// components below run against missing data and throw.
					<div className="p-5 text-center">
						<h5 className="mb-2">
							{loadError.isUnauthorized
								? 'Your session has expired'
								: "We couldn't load your subscription"}
						</h5>
						<p
							className="mb-4"
							style={{ color: '#64748B' }}
						>
							{loadError.isUnauthorized
								? 'Please sign in again to view your subscription and billing details.'
								: loadError.isNetworkError
									? 'We could not reach the server. Check your connection and try again.'
									: (loadError.message ?? 'Something went wrong. Please try again.')}
						</p>
						{!loadError.isUnauthorized && (
							<Button
								type="button"
								className="btn-primary fs-5 fw-light py-1 px-4"
								button={isloadSubscription ? 'Retrying…' : 'Try again'}
								onClick={getSubscriptionDetails}
							/>
						)}
					</div>
				) : (
					<div className="p-3">
						<CurrentPlanComponents
							currentPlan={subscriptionDetails}
							setIsOpenCancelSubscriptionModal={setIsOpenCancelSubscriptionModal}
							setSubscriptionScreenType={setSubscriptionScreenType}
						/>
						<SubscriptionStatisticsUsages
							// Optional chaining: this exact read crashed the page when the
							// state held `undefined` after a failed load.
							planUsages={subscriptionDetails?.plan_usages}
							setIsOpenUsageOverviewModal={setIsOpenUsageOverviewModal}
						/>
						<PlanBenefit benefits={planBenefit} />
						<SubscriptionFeatures
							features={subscriptionDetails?.features}
							loader={isloadPlan}
						/>
					</div>
				)}
			</div>
			{isOpenCancelSubscriptionModal && (
				<CancelSubscriptionModel
					isOpenModal={isOpenCancelSubscriptionModal}
					setIsOpenModal={setIsOpenCancelSubscriptionModal}
					setIsOpenReasonModal={setIsOpenReasonModal}
				/>
			)}
			{isOpenUsageOverviewModal && (
				<UsageOverviewModal
					isOpenModal={isOpenUsageOverviewModal}
					setIsOpenModal={setIsOpenUsageOverviewModal}
					planType={subscriptionDetails?.plan?.name}
					setIsOpenUpgradeModal={setIsOpenUpgradeModal}
				/>
			)}

			{isOpenReasonModal && (
				<CancelSubscriptionReasonModal
					isOpenModal={isOpenReasonModal}
					setIsOpenModal={setIsOpenReasonModal}
					showMessage={showMessage}
					onCancelSuccess={() => {
						getSubscriptionDetails();
					}}
				/>
			)}
			{isShowToast && (
				<CustomToastMessage
					icon={checkIcon}
					title={'Subscription cancelled successfully'}
					description={
						'You can continue using Pharmnex till your current plan ends. You will have 30 days after expiry to export your data or renew your subscription.'
					}
				/>
			)}
		</div>
	) : subscriptionScreenType === 'manage-billing' ? (
		<div style={{ height: 'calc(100vh - 80px)', overflowX: 'hidden', overflowY: 'auto' }}>
			<ManageBilling setSubscriptionScreenType={setSubscriptionScreenType} />
		</div>
	) : (
		<></>
	);
}
